import React, { useEffect, useRef, useState } from "react";
import { Table, Button, Form } from "react-bootstrap";
import Layout from "../../components/Layout";
import { useLocation, useNavigate } from "react-router-dom";
import ToastFile from "../../utils/ToastFile";
import { getAllTaskLogbyContractor } from "../../api/TaskApi";
import moment from "moment";
import Select from "react-select";
import {
  getAllEmployeebyContractor,
  getEmployeeDetails,
} from "../../api/EmployeeApi";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getTenantDetails } from "../../api/TenantApi";
import { addPaySlip } from "../../api/ReportApi";

const GenerateSlip = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [employeeDetails, setEmployeeDetails] = useState({});
  const [tenantDetails, setTenantDetails] = useState({});
  const [filteredLogs, setFilteredLogs] = useState([]);
  const fetched = useRef(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const slipRef = useRef();
  const [salarySlip, setSalarySlip] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768); // change 768 to your breakpoint
    };

    checkMobile(); // check on load
    window.addEventListener("resize", checkMobile); // update on resize

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    if (state?.selectedEmployee) {
      getEmployeeDetails(state?.selectedEmployee).then((res) => {
        if (res.status === 200) {
          setEmployeeDetails(res?.data?.data || {});
        }
      });
      getTenantDetails(user?.tenant_id).then((res) => {
        if (res.status === 200) {
          setTenantDetails(res?.data?.data || {});
        }
      });
      handleGenerate();
    }
  }, [user?.id]);

  const handleGenerate = async () => {
    getAllTaskLogbyContractor(
      user.tenant_id,
      state.selectedEmployee,
      state?.startDate,
      state?.endDate
    )
      .then((res) => {
        if (res.status === 200) {
          const filteredLogs = res.data.data.taskLogs.filter(
            (el) => el.isApproved === "Yes"
          );
          if (filteredLogs.length === 0) {
            ToastFile(
              "No record found for this employee in selected period",
              "error"
            );
            navigate("/payslip");
            setSalarySlip(null);
            return;
          }
          setFilteredLogs(filteredLogs);
        } else {
          ToastFile("Failed to fetch tasks", "error");
        }
      })
      .catch((err) => {
        console.error("Error fetching tasks:", err);
        ToastFile("Error fetching tasks", "error");
      });
  };

  const handleDownloadPDF = async () => {
    const input = slipRef.current;
    if (!input) return;
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save(`payslip_${state?.startDate}_to_${state?.endDate}.pdf`);
  };

  const handleDownloadMobilePDF = async () => {
    const input = slipRef.current;
    if (!input) return;

    setIsGeneratingPDF(true);

    try {
      // A4 size in points and pixels
      const a4WidthPt = 595.28;
      const a4HeightPt = 841.89;
      const a4WidthPx = 794; // 595.28pt at 96dpi

      // Clone and style for PDF
      const clone = input.cloneNode(true);
      clone.classList.add("invoice-pdf-desktop");
      clone.style.width = `${a4WidthPx}px`;
      clone.style.minWidth = `${a4WidthPx}px`;
      clone.style.maxWidth = `${a4WidthPx}px`;
      clone.style.margin = "0";
      clone.style.padding = "0";
      clone.style.background = "#fff";
      clone.style.boxSizing = "border-box";

      // Force all children to use full width
      Array.from(clone.querySelectorAll("*")).forEach((el) => {
        el.style.width = "100%";
        el.style.maxWidth = "100%";
        el.style.boxSizing = "border-box";
      });

      document.body.appendChild(clone);

      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(clone, {
        scale: 1, // Use scale 1 for mobile compatibility
        width: a4WidthPx,
        height: clone.scrollHeight,
        useCORS: true,
        backgroundColor: "#fff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
      });

      // Calculate image height in PDF points
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = a4WidthPt;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      let position = 0;
      let heightLeft = pdfHeight;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
      heightLeft -= a4HeightPt;

      while (heightLeft > 0) {
        position = heightLeft - pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= a4HeightPt;
      }

      pdf.save(`payslip_${state?.startDate}_to_${state?.endDate}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      const clone = document.querySelector(".invoice-pdf-desktop");
      if (clone) {
        document.body.removeChild(clone);
      }
      setIsGeneratingPDF(false);
    }
  };

  //calculate total amount
  const totalClaimHours = filteredLogs?.reduce(
    (sum, log) => sum + (parseFloat(log.claimHours) || 0),
    0
  );

  const payrate = filteredLogs.length > 0 ? filteredLogs.reduce((sum, log) => sum + parseFloat(log?.payrate ? log?.payrate : 0), 0) / filteredLogs.length : 0;

  const totalAmount = filteredLogs?.reduce(
    (sum, log) =>
      sum +
      parseFloat(log.claimHours) *
        parseFloat(log.payrate ? parseFloat(log.payrate) : 0),
    0
  );
  filteredLogs?.map((log) => {
    const prod =
      parseFloat(log.claimHours) * (log.payrate ? parseFloat(log.payrate) : 0);
  });

  const totalFuelCost = filteredLogs?.reduce(
    (sum, log) => sum + (parseFloat(log.fuelCost) || 0),
    0
  );

  const Total = totalAmount + totalFuelCost;

  const handleSavePaySlip = async () => {
    let payload = {
      employee_id: employeeDetails._id,
      slipNumber: employeeDetails?.name + moment().format("D-MMM-YYYY"),
      date: new Date(),
      startDate: new Date(state?.startDate),
      endDate: new Date(state?.endDate),
      companyName: tenantDetails.name,
      ordinaryHours: totalClaimHours,
      totalAmount: totalAmount,
      tenant_id: user.tenant_id,
      createdBy: user.user_id,
      createdAt: new Date(),
    };
    addPaySlip(payload)
      .then((res) => {
        if (res.status === 201) {
          ToastFile("Pay Slip added successfully", "added");
          setTimeout(() => {
            navigate("/payslip");
          }, 2000);
        } else {
          ToastFile("Failed to add Pay Slip", "error");
        }
      })
      .catch((err) => {
        console.error("Error adding pay slip:", err);
        ToastFile("Error adding pay slip", "error");
      });

    if (isMobile) {
      handleDownloadMobilePDF();
    } else {
      handleDownloadPDF();
    }
  };

  return (
    <Layout>
      <div className="w-100" style={{ maxWidth: 15000 }}>
        <div
          ref={slipRef}
          className="salary-slip mt-4 p-4 border rounded bg-white"
          style={{ maxWidth: 15000 }}
        >
          <div style={{ backgroundColor: "#e5e5dd" }} className="mb-3 p-2">
            <div className="d-flex justify-content-center">
              <span
            style={{
              fontSize: "30px",
              fontWeight: 600,
              minWidth: "140px",
            }}
          >
          Pay Slip
          </span>
            </div>

            <div className="d-flex justify-content-end">
              <div className="d-flex flex-column gap-2">
                <span>Payment Date : {moment().format("D-MMM-YYYY")} </span>
                <span>
                  Slip No :{" "}
                  {employeeDetails?.name + moment().format("DD-MM-YYYY")}
                </span>
                <span>
                  {" "}
                  <strong>Pay Period : </strong>{" "}
                  {moment(state?.startDate).format("DD-MM-YYYY")} to{" "}
                  {moment(state?.endDate).format("DD-MM-YYYY")}
                </span>
              </div>
            </div>
          </div>
          <div
            className="d-flex justify-content-between mb-3 p-2"
            style={{ backgroundColor: "#e5e5dd" }}
          >
            <div className="d-flex flex-column gap-3">
              <div>Name: {employeeDetails?.name || "Employee Name"}</div>
              <div>ABN: {employeeDetails?.abn || "-"}</div>
              <div>Contact No: {employeeDetails?.contact_no || "-"}</div>
              <div>Address: {employeeDetails?.address || "-"}</div>
            </div>
            <div className="d-flex flex-column gap-3">
              <div>Company Name: {tenantDetails.name}</div>
              <div>ABN: {tenantDetails.abn || "-"}</div>
              <div>Contact No: {tenantDetails.contact_no || "-"}</div>
              <div>Address: {tenantDetails.address || "-"}</div>
            </div>
          </div>
          <hr />
          <Table className="table">
            <thead>
              <tr style={{ textAlign: "center" }}>
                <th>Ordinary Hours</th>
                <th>Rate</th>
                <th>Additional Cost</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ textAlign: "center" }}>{totalClaimHours}</td>
                <td style={{ textAlign: "center" }}>${(payrate || 0).toFixed(2)}</td>
                <td style={{ textAlign: "center" }}>
                  ${totalFuelCost.toFixed(2)}
                </td>
                <td style={{ textAlign: "center" }}>${Total.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-center gap-3">
          <Button
            variant="dark"
            className="mb-3 mt-2"
            onClick={() => {
              navigate("/payslip");
            }}
          >
            Go Back
          </Button>
          <Button
            variant="success"
            className="mb-3 mt-2"
            onClick={handleSavePaySlip}
          >
            Save & Download
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default GenerateSlip;
